import React, { useEffect, useState } from 'react';
import { PaymentDialog, PriceItem } from '../PaymentDialog';
import { useStateSelector } from '../../../store/selectors';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import createNotification from '../../../utils/createNotification';
import {
    BuyerSelectionType,
    BuyerSelectionTypeOptions,
} from '../../../wallet/models/BuyerSelectionType';

interface BuyerSelectionPaymentComponentProps {
    isVisible: boolean;
    setIsVisible: (isVisible: React.SetStateAction<boolean>) => void;
    paymentSuccess: (transactionId: string) => void;
    engagementId: number;
    eventId: string;
    selectedBuyers: number[];
    pricePerBuyer: number;
    totalPrice: number;
    firstNAsOneCount: number;
    actionType: BuyerSelectionType;
    isFinalLock?: boolean;
    creditablePortion: number;
}

export interface SelectionPaymentDetails {
    selectedBuyerIds: number[];
    pricePerBuyer: number;
    totalPrice: number;
    firstNAsOneCount: number;
    eventId: string;
    creditablePortion: number;
}

const BuyerSelectionPaymentComponent = (
    props: BuyerSelectionPaymentComponentProps
) => {
    const axios = useStateSelector((state) => state.core.axios);
    const stripe = useStripe();
    const elements = useElements();

    const [originalServicePrice, setOriginalServicePrice] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [appliedCredits, setAppliedCredits] = useState(0);
    const [storeCreditsBalance, setStoreCreditsBalance] = useState(0);
    const [isPaymentInProcess, setIsPaymentInProcess] = useState(false);
    const dataNdaTooltipText = '<div>If a Buyer <em>does not execute</em> the Data NDA, the Seller will be reimbursed for the NDA fee.<br/>If the Buyer <em>executes</em> the NDA but the Seller fails to counter-execute, no refund will be issued.</div>';
    const idNdaTooltipText = '<div>If a Buyer <em>does not execute</em> the ID NDA, the Seller will be reimbursed for the NDA fee.</div>'

    useEffect(() => {
        setOriginalServicePrice(props.totalPrice);
    }, []);

    const calculateTotalPrice = () => originalServicePrice - appliedCredits;

    useEffect(() => {
        setTotalPrice(calculateTotalPrice());
    }, [appliedCredits, originalServicePrice]);

    // #1410: only the store-credit share of applied credits is non-creditable (flex and
    // reward credits keep earning credit). The wallet spends Store -> Flex -> Reward, so
    // that share is min(applied, store balance) — same math the backend stamps.
    const storeCreditsApplied = Math.min(appliedCredits, storeCreditsBalance);
    const creditablePortionAfterCredits =
        props.totalPrice > 0
            ? (props.creditablePortion *
                  Math.max(0, props.totalPrice - storeCreditsApplied)) /
              props.totalPrice
            : props.creditablePortion;

    const priceList: PriceItem[] = [
        {
            priceType: 'summary',
            label: BuyerSelectionTypeOptions.find(
                (option) => option.value === props.actionType
            )?.label + ' Fee',
            ...(props.actionType === BuyerSelectionType.IdNda || props.actionType === BuyerSelectionType.DataNda ? {
                tooltipText: props.actionType === BuyerSelectionType.IdNda ? idNdaTooltipText : dataNdaTooltipText
            } : {})
        },
        {
            priceType: 'regular',
            label: 'Fee per Buyer:',
            value: props.pricePerBuyer,
            valueType: 'currency',
        },
        {
            priceType: 'regular',
            label: '# of Selected Buyers:',
            value: props.selectedBuyers.length,
            valueType: 'number',
        },
        {
            priceType: 'summary',
            label: 'Total Due:',
            value: props.totalPrice,
            valueType: 'currency',
        },
        {
            priceType: 'regular',
            label: 'Creditable Portion:',
            value: creditablePortionAfterCredits,
            valueType: 'currency',
        },
    ];

    const directPayment = () => purchaseSelectedBuyers(false);

    const purchaseSelectedBuyers = (
        shouldUseSavedPaymentMethod: boolean,
        paymentTokenId: string = ''
    ) => {
        setIsPaymentInProcess(true);
        axios
            .post('/api/MarketplaceCommerce/BuyerSelectionPurchase', {
                walletId: 0,
                engagementId: props.engagementId,
                eventId: props.eventId,
                useWalletBalance: !!appliedCredits,
                selectedBuyerIds: props.selectedBuyers,
                walletAmount: appliedCredits,
                useSavedPaymentMethod: shouldUseSavedPaymentMethod,
                source: paymentTokenId,
                selectionType: props.actionType,
                totalPrice: originalServicePrice,
                isFinalLock: props.isFinalLock,
            })
            .then((response: any) => {
                if (response?.response?.status == 402) {
                    createNotification(
                        response.response.data?.title ??
                            'An unexpected error occured. Please try again later',
                        'error'
                    );
                    setIsPaymentInProcess(false);
                    props.setIsVisible(false);
                } else {
                    handlePricingUpdatedError(response);
                    setTimeout(() => {
                        setIsPaymentInProcess(false);
                        props.paymentSuccess(response.data);
                    }, 3000);
                }
            })
            .catch((err: any) => console.log(err));
    };

    const handlePricingUpdatedError = (response: any) => {
        if (response.response?.status === 422) {
            createNotification(
                'Buyer Seleciton Fee has been changed, please try again',
                'error'
            );
            setIsPaymentInProcess(false);
            props.setIsVisible(false);
            throw new Error('Price changed');
        }

        if (response.response?.status === 500) {
            createNotification(
                response.response.data?.title ??
                    'An unexpected error occured. Please try again later',
                'error'
            );
            setIsPaymentInProcess(false);
            props.setIsVisible(false);
            throw new Error('Bad request');
        }
    };

    return (
        <div className="buyer-selection-payment-component">
            <PaymentDialog
                isVisible={props.isVisible}
                setIsVisible={props.setIsVisible}
                stripe={stripe}
                elements={elements}
                priceList={priceList}
                directPayment={directPayment}
                originalServicePrice={originalServicePrice}
                servicePrice={originalServicePrice}
                totalProductPrice={totalPrice}
                apliedCredits={appliedCredits}
                setAppliedCredits={(value) => setAppliedCredits(value)}
                onStoreCreditsLoaded={(value) => setStoreCreditsBalance(value)}
                chargeRequest={purchaseSelectedBuyers}
                isParrentLoading={false}
                isPaymentInProcess={isPaymentInProcess}
                isAuthorizationRequired={false}></PaymentDialog>
        </div>
    );
};
export default BuyerSelectionPaymentComponent;
